/**
 * src/lib/knowledge/glossary.ts
 * Master glossary of foundational concepts across logic, epistemology, philosophy,
 * critical reasoning, and science.
 *
 * Implements strictly the required schema:
 * GlossaryEntry = {
 *   id: string;
 *   term: string;
 *   definition: string; (accurate, 1-3 sentences, plain language)
 *   example?: string;
 *   related: string[]; (other term ids - all strictly validated against the master list)
 *   seeAlso?: { label: string; to: string }[]; (links to spec pages, anchors, or simulation)
 *   status?: StatusId;
 *   category?: "epistemology" | "logic" | "philosophy" | "reasoning" | "science";
 * }
 */

import type { StatusId } from "./status";

export type GlossaryCategory =
  | "epistemology"
  | "logic"
  | "philosophy"
  | "reasoning"
  | "science";

export interface GlossarySeeAlso {
  label: string;
  to: string;
}

export interface GlossaryEntry {
  id: string;
  term: string;
  definition: string;
  example?: string;
  related: string[];
  seeAlso?: GlossarySeeAlso[];
  status?: StatusId;
  category?: GlossaryCategory;
}

export const GLOSSARY: GlossaryEntry[] = [
  {
    id: "a-posteriori",
    term: "A Posteriori",
    definition: "Knowledge or justification that depends directly on empirical evidence or sensory experience. Such propositions cannot be known through pure intellectual deduction alone.",
    example: "Knowing that light travels at approximately 300,000 km/s in a vacuum requires physical measurement and observation.",
    related: ["a-priori", "empirical", "evidence", "empiricism"],
    seeAlso: [
      { label: "Knowledge Laboratory", to: "/KnowledgeLaboratoryPage" },
      { label: "Evidence Comparison", to: "/KnowledgeLaboratoryPage#evidence-comparison" },
    ],
    status: "empirical",
    category: "epistemology",
  },
  {
    id: "a-priori",
    term: "A Priori",
    definition: "Knowledge or justification that can be known independently of sensory experience, relying solely on conceptual analysis, formal deduction, or definitions. Its warrant does not rest on ongoing empirical observation.",
    example: "Knowing that all bachelors are unmarried, or that 7 + 5 = 12, requires grasping definitions rather than conducting a field survey.",
    related: ["a-posteriori", "analytic-synthetic", "deduction", "rationalism"],
    seeAlso: [
      { label: "Knowledge Laboratory", to: "/KnowledgeLaboratoryPage" },
      { label: "Philosophy as Investigation", to: "/PhilosophyAsInvestigationPage" },
    ],
    status: "logical",
    category: "epistemology",
  },
  {
    id: "abduction",
    term: "Abduction",
    definition: "A mode of inference that begins with an observation and forms the most likely or explanatory hypothesis to account for it. Often termed inference to the best explanation.",
    example: "Observing wet grass and grey clouds in the morning leads to the abductive inference that it rained overnight, rather than that a neighbor sprayed a hose.",
    related: ["inference-to-the-best-explanation", "deduction", "induction", "hypothesis"],
    seeAlso: [
      { label: "Critical Thinking Workshop", to: "/CriticalThinkingPage" },
      { label: "Claim Analyzer", to: "/CriticalThinkingPage#claim-analyzer" },
    ],
    status: "philosophical",
    category: "reasoning",
  },
  {
    id: "absence-of-evidence",
    term: "Absence of Evidence",
    definition: "The state of lacking positive proof for a claim, which does not automatically constitute positive evidence for its falsity unless an exhaustive, highly sensitive investigation was conducted where evidence should have appeared.",
    example: "Not having detected microbial life under a single rock on Mars is an absence of evidence, not proof that Mars has never harbored microscopic organisms.",
    related: ["evidence", "skepticism", "burden-of-proof", "fallacy"],
    seeAlso: [
      { label: "Fallacy: Appeal to Ignorance", to: "/FallacyLibraryPage#fallacy-appeal-to-ignorance" },
      { label: "Critical Thinking Workshop", to: "/CriticalThinkingPage" },
    ],
    status: "philosophical",
    category: "reasoning",
  },
  {
    id: "ad-hominem",
    term: "Ad Hominem",
    definition: "An informal fallacy occurring when an argument attacks the personal traits, motives, background, or character of the advocate rather than engaging with the objective merits of their premise.",
    example: "Dismissing an astrophysicist's orbital calculation because you dislike their tone on social media commits an ad hominem error.",
    related: ["informal-fallacy", "fallacy", "tu-quoque", "genetic-fallacy"],
    seeAlso: [
      { label: "Fallacy: Ad Hominem", to: "/FallacyLibraryPage#fallacy-ad-hominem" },
      { label: "Critical Thinking Workshop", to: "/CriticalThinkingPage" },
    ],
    status: "philosophical",
    category: "reasoning",
  },
  {
    id: "affirming-the-consequent",
    term: "Affirming the Consequent",
    definition: "A formal logical fallacy where one infers the truth of an antecedent condition from the observed truth of its consequent conditional: If P then Q; Q; therefore P.",
    example: "If it rains, the street gets wet; the street is wet; therefore, it rained (ignoring street cleaners, broken fire hydrants, or melted snow).",
    related: ["formal-fallacy", "modus-ponens", "conditional", "invalid"],
    seeAlso: [
      { label: "Logic Laboratory", to: "/LogicLaboratoryPage" },
      { label: "Argument Tester", to: "/LogicLaboratoryPage#argument-tester" },
      { label: "Fallacy: Affirming the Consequent", to: "/FallacyLibraryPage#fallacy-affirming-the-consequent" },
    ],
    status: "logical",
    category: "logic",
  },
  {
    id: "ambiguous-definition",
    term: "Ambiguous Definition",
    definition: "The flaw of formulating a term or premise using phrasing that supports multiple conflicting interpretations, obscuring whether an argument is valid or sound.",
    example: "Arguing that 'natural foods are always good for health' without defining whether deadly hemlock counts as natural.",
    related: ["equivocation", "conceptual-analysis", "informal-fallacy"],
    seeAlso: [
      { label: "Revise the Definition", to: "/PhilosophyAsInvestigationPage#revise-the-definition" },
      { label: "Critical Thinking Workshop", to: "/CriticalThinkingPage" },
    ],
    status: "philosophical",
    category: "philosophy",
  },
  {
    id: "amphiboly",
    term: "Amphiboly",
    definition: "A fallacy of ambiguity resulting from loose or defective grammatical syntax rather than the double meaning of a single specific word.",
    example: "The sign read 'Save soap and waste paper', which could mean conserve soap and discard paper, or treat both soap and paper as wasteful.",
    related: ["equivocation", "informal-fallacy", "fallacy"],
    seeAlso: [
      { label: "Fallacy Library", to: "/FallacyLibraryPage" },
    ],
    status: "philosophical",
    category: "reasoning",
  },
  {
    id: "anecdotal-evidence",
    term: "Anecdotal Evidence",
    definition: "Evidence derived from isolated personal testimony, informal stories, or individual incidents rather than systematic sampling and controlled empirical measurement.",
    example: "Concluding that a dietary supplement cures arthritis because an uncle took it and reported feeling better two days later.",
    related: ["evidence", "empirical", "confirmation-bias", "hasty-generalization"],
    seeAlso: [
      { label: "Fallacy: Anecdotal Evidence", to: "/FallacyLibraryPage#fallacy-anecdotal-evidence" },
      { label: "Critical Thinking Workshop", to: "/CriticalThinkingPage" },
    ],
    status: "unsupported",
    category: "reasoning",
  },
  {
    id: "analytic-synthetic",
    term: "Analytic–Synthetic Distinction",
    definition: "The philosophical distinction between statements true purely by virtue of their meanings (analytic, such as 'all bachelors are unmarried') and statements whose truth depends on how the physical world actually is (synthetic).",
    example: "'Triangles have three sides' is analytic; 'this triangle is drawn in cyan chalk' is synthetic.",
    related: ["a-priori", "a-posteriori", "tautology", "conceptual-analysis"],
    seeAlso: [
      { label: "Philosophy as Investigation", to: "/PhilosophyAsInvestigationPage" },
      { label: "Knowledge Laboratory", to: "/KnowledgeLaboratoryPage" },
    ],
    status: "philosophical",
    category: "philosophy",
  },
  {
    id: "appeal-to-emotion",
    term: "Appeal to Emotion",
    definition: "An informal fallacy that substitutes an emotional reaction—such as pity, fear, or outrage—for relevant evidence and valid argumentation.",
    example: "Demanding that a flawed engineering proposal be funded because the inventor worked through personal heartbreak to complete it.",
    related: ["informal-fallacy", "fallacy", "red-herring"],
    seeAlso: [
      { label: "Fallacy: Appeal to Emotion", to: "/FallacyLibraryPage#fallacy-appeal-to-emotion" },
      { label: "Critical Thinking Workshop", to: "/CriticalThinkingPage" },
    ],
    status: "philosophical",
    category: "reasoning",
  },
  {
    id: "appeal-to-ignorance",
    term: "Appeal to Ignorance",
    definition: "An informal fallacy asserting that a proposition must be true simply because it has not yet been proven false, or false simply because it has not yet been proven true.",
    example: "Claiming that telepathic signals exist because scientists haven't conclusively disproven every conceivable psychic mechanism.",
    related: ["informal-fallacy", "absence-of-evidence", "burden-of-proof"],
    seeAlso: [
      { label: "Fallacy: Appeal to Ignorance", to: "/FallacyLibraryPage#fallacy-appeal-to-ignorance" },
      { label: "Critical Thinking Workshop", to: "/CriticalThinkingPage" },
    ],
    status: "philosophical",
    category: "reasoning",
  },
  {
    id: "appeal-to-irrelevant-authority",
    term: "Appeal to Irrelevant Authority",
    definition: "Citing the endorsement of a prominent individual whose prestige or expertise lies outside the specialized domain of the claim being evaluated.",
    example: "Citing a Nobel laureate in literature to substantiate an unproven claim regarding subatomic quantum entanglement.",
    related: ["expertise", "testimony", "informal-fallacy", "primary-source"],
    seeAlso: [
      { label: "Fallacy: Appeal to Authority", to: "/FallacyLibraryPage#fallacy-appeal-to-irrelevant-authority" },
      { label: "Claim Analyzer", to: "/CriticalThinkingPage#claim-analyzer" },
    ],
    status: "philosophical",
    category: "reasoning",
  },
  {
    id: "appeal-to-novelty",
    term: "Appeal to Novelty",
    definition: "The fallacy of claiming that an idea, policy, or invention is inherently superior simply because it is newer, modern, or recently discovered.",
    example: "Assuming that a newly proposed nutritional theory is more accurate than 50 years of biochemistry research merely because it was published this week.",
    related: ["appeal-to-tradition", "informal-fallacy", "fallacy"],
    seeAlso: [
      { label: "Fallacy: Appeal to Novelty", to: "/FallacyLibraryPage#fallacy-appeal-to-novelty" },
    ],
    status: "philosophical",
    category: "reasoning",
  },
  {
    id: "appeal-to-popularity",
    term: "Appeal to Popularity",
    definition: "An informal fallacy asserting that a proposition is true or desirable merely because a large majority or crowd of people believe it.",
    example: "Arguing that the Earth was flat in medieval times because thousands of people held that intuition.",
    related: ["informal-fallacy", "fallacy", "evidence", "testimony"],
    seeAlso: [
      { label: "Fallacy: Appeal to Popularity", to: "/FallacyLibraryPage#fallacy-appeal-to-popularity" },
    ],
    status: "philosophical",
    category: "reasoning",
  },
  {
    id: "appeal-to-tradition",
    term: "Appeal to Tradition",
    definition: "An informal fallacy arguing that an action or belief is justified solely because it has been practiced or accepted for generations.",
    example: "Maintaining that medical diagnoses should rely on herbal folklore simply because our ancestors did so for four centuries.",
    related: ["appeal-to-novelty", "informal-fallacy", "fallacy"],
    seeAlso: [
      { label: "Fallacy: Appeal to Tradition", to: "/FallacyLibraryPage#fallacy-appeal-to-tradition" },
    ],
    status: "philosophical",
    category: "reasoning",
  },
  {
    id: "argument",
    term: "Argument",
    definition: "A structured series of statements comprising one or more premises intended to provide inferential support, justification, or proof for a concluding proposition.",
    example: "'Premise 1: All humans are mortal. Premise 2: Socrates is human. Conclusion: Therefore, Socrates is mortal.'",
    related: ["premise", "conclusion", "valid", "sound", "deduction"],
    seeAlso: [
      { label: "Critical Thinking Workshop", to: "/CriticalThinkingPage" },
      { label: "Logic Laboratory", to: "/LogicLaboratoryPage" },
      { label: "Argument Tester", to: "/LogicLaboratoryPage#argument-tester" },
    ],
    status: "logical",
    category: "logic",
  },
  {
    id: "assumption",
    term: "Assumption",
    definition: "A premise, presupposition, or condition accepted as true or given without explicit defense or argument within a specific line of reasoning.",
    example: "Assuming that physical laws remain uniform across unobserved regions of the cosmos when analyzing distant stellar spectra.",
    related: ["premise", "argument", "fictional", "foundationalism"],
    seeAlso: [
      { label: "Critical Thinking Workshop", to: "/CriticalThinkingPage" },
      { label: "Claim Analyzer", to: "/CriticalThinkingPage#claim-analyzer" },
    ],
    status: "philosophical",
    category: "reasoning",
  },
  {
    id: "base-rate",
    term: "Base Rate",
    definition: "The unconditional baseline frequency, incidence, or percentage of an event or characteristic occurring within a given reference population.",
    example: "If a rare disease affects only 1 in 10,000 individuals, the base rate is 0.01%, meaning even a 99% accurate test may yield more false positives than true positives.",
    related: ["base-rate-neglect", "conditional-probability-confusion", "uncertainty"],
    seeAlso: [
      { label: "Fallacy: Base Rate Neglect", to: "/FallacyLibraryPage#fallacy-base-rate-neglect" },
      { label: "Critical Thinking Workshop", to: "/CriticalThinkingPage" },
    ],
    status: "empirical",
    category: "science",
  },
  {
    id: "base-rate-neglect",
    term: "Base Rate Neglect",
    definition: "A cognitive and probabilistic error where judgment focuses excessively on specific individual case features while ignoring the prior statistical prevalence of the phenomenon in the population.",
    example: "Believing a person is almost certainly a librarian after hearing a description of their introversion, while ignoring that sales clerks outnumber librarians 50 to 1.",
    related: ["base-rate", "conditional-probability-confusion", "cognitive-bias"],
    seeAlso: [
      { label: "Fallacy: Base Rate Neglect", to: "/FallacyLibraryPage#fallacy-base-rate-neglect" },
    ],
    status: "empirical",
    category: "reasoning",
  },
  {
    id: "begging-the-question",
    term: "Begging the Question",
    definition: "An informal fallacy in which an argument's premises secretly assume the truth of the conclusion they are supposed to establish, yielding circular justification.",
    example: "'Miracles are impossible because any reported event violating natural law simply could not have occurred.'",
    related: ["circular-reasoning", "informal-fallacy", "premise"],
    seeAlso: [
      { label: "Fallacy: Begging the Question", to: "/FallacyLibraryPage#fallacy-begging-the-question" },
      { label: "Critical Thinking Workshop", to: "/CriticalThinkingPage" },
    ],
    status: "philosophical",
    category: "reasoning",
  },
  {
    id: "belief",
    term: "Belief",
    definition: "A psychological or cognitive state in which an agent takes an attitude of acceptance or assent toward a proposition, regarding it as true.",
    example: "Holding the belief that the Sun will rise tomorrow, which may or may not be accompanied by conclusive justification.",
    related: ["truth", "justification", "knowledge", "justified-true-belief"],
    seeAlso: [
      { label: "Knowledge Laboratory", to: "/KnowledgeLaboratoryPage" },
      { label: "Philosophy as Investigation", to: "/PhilosophyAsInvestigationPage" },
    ],
    status: "philosophical",
    category: "epistemology",
  },
  {
    id: "biconditional",
    term: "Biconditional",
    definition: "A compound logical connective expressing that two propositions have the exact same truth value: 'P if and only if Q' (P ↔ Q). It is true when both are true or both are false.",
    example: "'A polygon is a triangle if and only if it has exactly three internal angles.'",
    related: ["conditional", "necessary-condition", "sufficient-condition", "tautology"],
    seeAlso: [
      { label: "Truth Table Tool", to: "/LogicLaboratoryPage#truth-table" },
      { label: "Logic Laboratory", to: "/LogicLaboratoryPage" },
    ],
    status: "logical",
    category: "logic",
  },
  {
    id: "burden-of-proof",
    term: "Burden of Proof",
    definition: "The epistemic obligation placed on the party making an affirmative or non-default claim to provide adequate justification, reasoning, or evidence for it.",
    example: "If someone claims that an invisible teapot orbits Jupiter, the burden of proof rests on them to provide evidence, not on others to disprove it.",
    related: ["appeal-to-ignorance", "evidence", "skepticism", "fallacy"],
    seeAlso: [
      { label: "Critical Thinking Workshop", to: "/CriticalThinkingPage" },
      { label: "Fallacy: Appeal to Ignorance", to: "/FallacyLibraryPage#fallacy-appeal-to-ignorance" },
    ],
    status: "philosophical",
    category: "reasoning",
  },
  {
    id: "causation",
    term: "Causation",
    definition: "A direct generative relationship between two events wherein the occurrence of the cause produces, brings about, or determines the occurrence of the effect.",
    example: "Striking a cue ball into the eight ball transfers kinetic energy, causing the eight ball to accelerate across the table.",
    related: ["correlation", "confounding", "post-hoc", "empirical"],
    seeAlso: [
      { label: "Fallacy: Correlation vs Causation", to: "/FallacyLibraryPage#fallacy-correlation-causation" },
      { label: "Critical Thinking Workshop", to: "/CriticalThinkingPage" },
    ],
    status: "empirical",
    category: "science",
  },
  {
    id: "certainty",
    term: "Certainty",
    definition: "The epistemic condition of being completely free from doubt or the possibility of error. Most contemporary epistemologists distinguish psychological conviction from objective certainty.",
    example: "Descartes argued that 'I think, therefore I exist' possesses indubitable certainty, whereas empirical claims rarely achieve absolute certainty.",
    related: ["fallibilism", "skepticism", "justification", "knowledge"],
    seeAlso: [
      { label: "Knowledge Laboratory", to: "/KnowledgeLaboratoryPage" },
      { label: "Philosophy as Investigation", to: "/PhilosophyAsInvestigationPage" },
    ],
    status: "philosophical",
    category: "epistemology",
  },
  {
    id: "cherry-picking",
    term: "Cherry-Picking",
    definition: "The selective presentation of evidence, studies, or observations that favor a predetermined stance while systematically suppressing or ignoring contradictory data.",
    example: "Highlighting three cold winter days in one city to dispute global climatic warming trends while ignoring centuries of planetary temperature data.",
    related: ["confirmation-bias", "informal-fallacy", "selective-reporting"],
    seeAlso: [
      { label: "Fallacy: Cherry-Picking", to: "/FallacyLibraryPage#fallacy-cherry-picking" },
      { label: "Critical Thinking Workshop", to: "/CriticalThinkingPage" },
    ],
    status: "unsupported",
    category: "reasoning",
  },
  {
    id: "circular-reasoning",
    term: "Circular Reasoning",
    definition: "A defect in reasoning where the conclusion is directly or indirectly used as a supporting premise, meaning the argument assumes the very thing it aims to prove.",
    example: "'The book is entirely infallible because it is dictated by divine truth, and we know divine truth exists because the book says so.'",
    related: ["begging-the-question", "informal-fallacy", "premise"],
    seeAlso: [
      { label: "Fallacy: Circular Reasoning", to: "/FallacyLibraryPage#fallacy-circular-reasoning" },
      { label: "Critical Thinking Workshop", to: "/CriticalThinkingPage" },
    ],
    status: "philosophical",
    category: "reasoning",
  },
  {
    id: "cognitive-bias",
    term: "Cognitive Bias",
    definition: "A systematic, subconscious pattern of deviation from norm, logic, or rational probability in human judgment, judgment formation, and decision-making.",
    example: "Anchoring bias, where people rely too heavily on the first piece of information encountered when estimating the value of an unknown item.",
    related: ["confirmation-bias", "base-rate-neglect", "fallacy"],
    seeAlso: [
      { label: "Critical Thinking Workshop", to: "/CriticalThinkingPage" },
      { label: "Fallacy Library", to: "/FallacyLibraryPage" },
    ],
    status: "empirical",
    category: "reasoning",
  },
  {
    id: "coherentism",
    term: "Coherentism",
    definition: "The epistemic theory that a belief is justified not by resting on foundational basic beliefs, but by fitting into a mutually reinforcing, harmonious web of other beliefs.",
    example: "A detective's theory of a crime is justified because physical evidence, witness accounts, timelines, and motives all interlock without contradiction.",
    related: ["foundationalism", "infinitism", "justification", "knowledge"],
    seeAlso: [
      { label: "Knowledge Laboratory", to: "/KnowledgeLaboratoryPage" },
    ],
    status: "philosophical",
    category: "epistemology",
  },
  {
    id: "composition",
    term: "Fallacy of Composition",
    definition: "The erroneous inference that because every individual part or member of a system possesses a certain attribute, the whole system must also possess that attribute.",
    example: "Arguing that because every brick in a building weighs under two kilograms, the entire building must weigh under two kilograms.",
    related: ["division", "informal-fallacy", "fallacy"],
    seeAlso: [
      { label: "Fallacy: Composition", to: "/FallacyLibraryPage#fallacy-composition" },
    ],
    status: "philosophical",
    category: "reasoning",
  },
  {
    id: "conceptual-analysis",
    term: "Conceptual Analysis",
    definition: "The core philosophical method of breaking complex, foundational concepts into their constituent necessary and sufficient conditions to clarify meaning and resolve paradoxes.",
    example: "Analyzing the concept of 'knowledge' into belief, truth, and justification to test whether all three together are sufficient.",
    related: ["necessary-condition", "sufficient-condition", "thought-experiment", "analytic-synthetic"],
    seeAlso: [
      { label: "Philosophy as Investigation", to: "/PhilosophyAsInvestigationPage" },
      { label: "Revise the Definition", to: "/PhilosophyAsInvestigationPage#revise-the-definition" },
    ],
    status: "philosophical",
    category: "philosophy",
  },
  {
    id: "conclusion",
    term: "Conclusion",
    definition: "The principal proposition in an argument whose truth or probability the premises are offered to establish or support.",
    example: "In the syllogism 'All metals expand when heated; iron is a metal; therefore, iron expands when heated', the final clause is the conclusion.",
    related: ["premise", "argument", "valid", "sound"],
    seeAlso: [
      { label: "Logic Laboratory", to: "/LogicLaboratoryPage" },
      { label: "Argument Tester", to: "/LogicLaboratoryPage#argument-tester" },
      { label: "Critical Thinking Workshop", to: "/CriticalThinkingPage" },
    ],
    status: "logical",
    category: "logic",
  },
  {
    id: "conditional",
    term: "Conditional Statement",
    definition: "A hypothetical proposition of the form 'If P, then Q', where P is the antecedent condition and Q is the consequent outcome.",
    example: "'If an atom absorbs a photon of sufficient energy, then an electron transitions to a higher energy level.'",
    related: ["biconditional", "modus-ponens", "modus-tollens", "necessary-condition", "sufficient-condition"],
    seeAlso: [
      { label: "Truth Table Tool", to: "/LogicLaboratoryPage#truth-table" },
      { label: "Logic Laboratory", to: "/LogicLaboratoryPage" },
    ],
    status: "logical",
    category: "logic",
  },
  {
    id: "conditional-probability-confusion",
    term: "Conditional Probability Confusion",
    definition: "The error of conflating the probability of event A given B with the probability of event B given A: P(A|B) ≠ P(B|A).",
    example: "Assuming that because most convicted bank robbers owned gloves, someone caught wearing gloves is almost certainly a bank robber.",
    related: ["base-rate", "base-rate-neglect", "prosecutors-fallacy"],
    seeAlso: [
      { label: "Fallacy: Prosecutor's Fallacy", to: "/FallacyLibraryPage#fallacy-prosecutors-fallacy" },
      { label: "Critical Thinking Workshop", to: "/CriticalThinkingPage" },
    ],
    status: "logical",
    category: "reasoning",
  },
  {
    id: "confirmation-bias",
    term: "Confirmation Bias",
    definition: "The human tendency to search for, interpret, favor, and recall evidence in a way that confirms prior hypotheses while disregarding disconfirming evidence.",
    example: "A researcher noticing only clinical trials showing a drug works while overlooking three replicated studies reporting no statistical benefit.",
    related: ["cognitive-bias", "cherry-picking", "falsifiability", "evidence"],
    seeAlso: [
      { label: "Critical Thinking Workshop", to: "/CriticalThinkingPage" },
      { label: "Claim Analyzer", to: "/CriticalThinkingPage#claim-analyzer" },
    ],
    status: "empirical",
    category: "reasoning",
  },
  {
    id: "confounding",
    term: "Confounding Variable",
    definition: "An unmeasured or extraneous factor that correlates with both the supposed cause and the supposed effect, creating a spurious statistical association.",
    example: "Ice cream sales and drowning rates correlate strongly because warmer summer weather increases both, not because eating ice cream causes drowning.",
    related: ["correlation", "causation", "empirical", "hypothesis"],
    seeAlso: [
      { label: "Fallacy: Confounding", to: "/FallacyLibraryPage#fallacy-confounding" },
      { label: "Critical Thinking Workshop", to: "/CriticalThinkingPage" },
    ],
    status: "empirical",
    category: "science",
  },
  {
    id: "conjunction",
    term: "Conjunction",
    definition: "A truth-functional connective ('and', denoted P ∧ Q) that produces a true proposition if and only if both conjuncts are simultaneously true.",
    example: "'Mars has two moons AND Venus has no moons' is true only because both component assertions are independently true.",
    related: ["disjunction", "conditional", "conjunction-fallacy", "tautology"],
    seeAlso: [
      { label: "Truth Table Tool", to: "/LogicLaboratoryPage#truth-table" },
      { label: "Logic Laboratory", to: "/LogicLaboratoryPage" },
    ],
    status: "logical",
    category: "logic",
  },
  {
    id: "conjunction-fallacy",
    term: "Conjunction Fallacy",
    definition: "A probabilistic error in which a person estimates that a compound condition (P and Q) is more probable than one of its single constituent conditions (P alone).",
    example: "Judging that Linda is more likely to be 'a bank teller active in the feminist movement' than simply 'a bank teller', defying basic probability axioms.",
    related: ["conjunction", "base-rate-neglect", "cognitive-bias"],
    seeAlso: [
      { label: "Fallacy: Conjunction Fallacy", to: "/FallacyLibraryPage#fallacy-conjunction-fallacy" },
    ],
    status: "logical",
    category: "reasoning",
  },
  {
    id: "consistency",
    term: "Consistency",
    definition: "The property of a set of propositions such that it is logically possible for all of them to be true at the exact same time without generating contradiction.",
    example: "The statements 'The lamp is on' and 'The room is dark' can be consistent if the lamp is shaded and the room is vast.",
    related: ["contradiction", "valid", "reductio-ad-absurdum"],
    seeAlso: [
      { label: "Logic Laboratory", to: "/LogicLaboratoryPage" },
      { label: "Truth Table Tool", to: "/LogicLaboratoryPage#truth-table" },
    ],
    status: "logical",
    category: "logic",
  },
  {
    id: "contingency",
    term: "Contingency",
    definition: "A proposition that is neither necessarily true in all possible worlds (tautology) nor necessarily false (contradiction), but whose truth depends on how things actually are.",
    example: "'The Earth has one natural moon' is a contingent truth; it could have had zero or two moons without violating the laws of logic.",
    related: ["tautology", "contradiction", "a-posteriori"],
    seeAlso: [
      { label: "Truth Table Tool", to: "/LogicLaboratoryPage#truth-table" },
      { label: "Philosophy as Investigation", to: "/PhilosophyAsInvestigationPage" },
    ],
    status: "logical",
    category: "logic",
  },
  {
    id: "contradiction",
    term: "Contradiction",
    definition: "A proposition or conjunction of statements that asserts both P and not-P at the same time and in the same respect, which is logically impossible to be true.",
    example: "'The particle is completely at rest AND the particle is moving at 100 km/s relative to this frame.'",
    related: ["consistency", "tautology", "reductio-ad-absurdum", "contingency"],
    seeAlso: [
      { label: "Truth Table Tool", to: "/LogicLaboratoryPage#truth-table" },
      { label: "Logic Laboratory", to: "/LogicLaboratoryPage" },
    ],
    status: "logical",
    category: "logic",
  },
  {
    id: "correlation",
    term: "Correlation",
    definition: "A mutual statistical relationship or association between two variables, such that changes in one accompany predictable changes in the other.",
    example: "A high statistical correlation between smoking prevalence and lung cancer rates across demographic groups.",
    related: ["causation", "confounding", "empirical"],
    seeAlso: [
      { label: "Fallacy: Correlation vs Causation", to: "/FallacyLibraryPage#fallacy-correlation-causation" },
      { label: "Critical Thinking Workshop", to: "/CriticalThinkingPage" },
    ],
    status: "empirical",
    category: "science",
  },
  {
    id: "counterexample",
    term: "Counterexample",
    definition: "A specific instance, specimen, or case that directly refutes a universal claim, rule, or proposed definition by demonstrating its failure.",
    example: "Presenting a black swan is a decisive counterexample to the universal generalization 'All swans are white.'",
    related: ["falsifiability", "valid", "conceptual-analysis", "thought-experiment"],
    seeAlso: [
      { label: "Revise the Definition", to: "/PhilosophyAsInvestigationPage#revise-the-definition" },
      { label: "Logic Laboratory", to: "/LogicLaboratoryPage" },
    ],
    status: "logical",
    category: "logic",
  },
  {
    id: "deduction",
    term: "Deduction",
    definition: "A form of reasoning in which the conclusion necessarily follows from the premises; if all premises are true and the argument form is valid, the conclusion cannot be false.",
    example: "'All prime numbers greater than 2 are odd. 17 is a prime number greater than 2. Therefore, 17 is odd.'",
    related: ["induction", "abduction", "valid", "sound", "argument"],
    seeAlso: [
      { label: "Logic Laboratory", to: "/LogicLaboratoryPage" },
      { label: "Argument Tester", to: "/LogicLaboratoryPage#argument-tester" },
    ],
    status: "logical",
    category: "logic",
  },
  {
    id: "denying-the-antecedent",
    term: "Denying the Antecedent",
    definition: "A formal logical fallacy where one infers the falsity of the consequent from the falsity of the antecedent: If P then Q; not P; therefore not Q.",
    example: "If you are a dog, you are a mammal. You are not a dog. Therefore, you are not a mammal (ignoring cats, whales, and humans).",
    related: ["formal-fallacy", "modus-tollens", "conditional", "invalid"],
    seeAlso: [
      { label: "Logic Laboratory", to: "/LogicLaboratoryPage" },
      { label: "Argument Tester", to: "/LogicLaboratoryPage#argument-tester" },
      { label: "Fallacy: Denying the Antecedent", to: "/FallacyLibraryPage#fallacy-denying-the-antecedent" },
    ],
    status: "logical",
    category: "logic",
  },
  {
    id: "descriptive-claim",
    term: "Descriptive Claim",
    definition: "A statement asserting how the world, reality, or state of affairs actually is, verifiable or falsifiable through empirical observation or logical analysis.",
    example: "'Carbon dioxide concentrations in the atmosphere exceed 420 parts per million.'",
    related: ["normative-claim", "empirical", "evidence"],
    seeAlso: [
      { label: "Critical Thinking Workshop", to: "/CriticalThinkingPage" },
      { label: "Claim Analyzer", to: "/CriticalThinkingPage#claim-analyzer" },
    ],
    status: "empirical",
    category: "philosophy",
  },
  {
    id: "disjunction",
    term: "Disjunction",
    definition: "A truth-functional connective ('or', denoted P ∨ Q) which, in standard logic, is inclusive: true if either conjunct is true, or if both are true.",
    example: "'The light switch is up OR the backup generator is running.'",
    related: ["conjunction", "disjunctive-syllogism", "conditional"],
    seeAlso: [
      { label: "Truth Table Tool", to: "/LogicLaboratoryPage#truth-table" },
      { label: "Logic Laboratory", to: "/LogicLaboratoryPage" },
    ],
    status: "logical",
    category: "logic",
  },
  {
    id: "disjunctive-syllogism",
    term: "Disjunctive Syllogism",
    definition: "A valid deductive inference rule of the form: P or Q; Not P; Therefore Q.",
    example: "The key is either in my coat or in the drawer. The key is not in my coat. Therefore, it is in the drawer.",
    related: ["disjunction", "deduction", "valid", "syllogism"],
    seeAlso: [
      { label: "Logic Laboratory", to: "/LogicLaboratoryPage" },
      { label: "Argument Tester", to: "/LogicLaboratoryPage#argument-tester" },
    ],
    status: "logical",
    category: "logic",
  },
  {
    id: "division",
    term: "Fallacy of Division",
    definition: "The erroneous inference that because an entire system or whole entity possesses a certain property, each of its individual constituent parts must also possess it.",
    example: "Arguing that because a supercomputer is capable of running complex molecular dynamics, a single screw in its chassis must also do molecular dynamics.",
    related: ["composition", "informal-fallacy", "fallacy"],
    seeAlso: [
      { label: "Fallacy: Division", to: "/FallacyLibraryPage#fallacy-division" },
    ],
    status: "philosophical",
    category: "reasoning",
  },
  {
    id: "dualism",
    term: "Dualism",
    definition: "The metaphysical position that reality consists of two fundamentally distinct categories of substances, properties, or entities, typically mental and physical.",
    example: "Cartesian substance dualism posited that the physical body (res extensa) and immaterial conscious mind (res cogitans) interact causally.",
    related: ["physicalism", "idealism", "metaphysics", "ontology"],
    seeAlso: [
      { label: "Philosophy as Investigation", to: "/PhilosophyAsInvestigationPage" },
    ],
    status: "philosophical",
    category: "philosophy",
  },
  {
    id: "empiricism",
    term: "Empiricism",
    definition: "The epistemological theory asserting that substantive knowledge arises primarily through sensory perception, experimental observation, and inductive generalization.",
    example: "Locke's argument that the mind begins as a blank slate (tabula rasa) upon which sensory experience writes.",
    related: ["rationalism", "a-posteriori", "evidence", "induction"],
    seeAlso: [
      { label: "Knowledge Laboratory", to: "/KnowledgeLaboratoryPage" },
      { label: "Philosophy as Investigation", to: "/PhilosophyAsInvestigationPage" },
    ],
    status: "philosophical",
    category: "epistemology",
  },
  {
    id: "epistemology",
    term: "Epistemology",
    definition: "The core branch of philosophy concerned with the nature, origin, scope, justification, and limits of knowledge and rational belief.",
    example: "Investigating whether belief accompanied by true justification is sufficient to count as knowledge in Gettier-style counterexamples.",
    related: ["knowledge", "belief", "justification", "skepticism", "truth"],
    seeAlso: [
      { label: "Knowledge Laboratory", to: "/KnowledgeLaboratoryPage" },
      { label: "Evidence Comparison", to: "/KnowledgeLaboratoryPage#evidence-comparison" },
    ],
    status: "philosophical",
    category: "epistemology",
  },
  {
    id: "equivocation",
    term: "Equivocation",
    definition: "An informal fallacy that exploits multiple distinct meanings of a single word or phrase within an argument, treating them as identical to reach a deceptive conclusion.",
    example: "'A feather is light. What is light cannot be dark. Therefore, a feather cannot be dark.'",
    related: ["ambiguous-definition", "informal-fallacy", "fallacy"],
    seeAlso: [
      { label: "Fallacy: Equivocation", to: "/FallacyLibraryPage#fallacy-equivocation" },
      { label: "Critical Thinking Workshop", to: "/CriticalThinkingPage" },
    ],
    status: "philosophical",
    category: "reasoning",
  },
  {
    id: "estimate-as-exact",
    term: "Estimate as Exact",
    definition: "The misleading presentation of rough heuristic approximations, model extrapolations, or speculative estimates as if they were pin-point empirical measurements.",
    example: "Stating that exactly 4,218,912 alien civilizations exist in the galaxy based solely on speculative parameter inputs into the Drake Equation.",
    related: ["ignoring-intervals", "uncertainty", "empirical"],
    seeAlso: [
      { label: "Fallacy Library", to: "/FallacyLibraryPage" },
      { label: "Critical Thinking Workshop", to: "/CriticalThinkingPage" },
    ],
    status: "unsupported",
    category: "reasoning",
  },
  {
    id: "evidence",
    term: "Evidence",
    definition: "Facts, documentation, observational data, or reasoned demonstrations that serve to increase or decrease the epistemic probability of a hypothesis.",
    example: "The detection of the cosmic microwave background radiation provided decisive evidence supporting the Big Bang cosmological model.",
    related: ["justification", "empirical", "hypothesis", "testimony"],
    seeAlso: [
      { label: "Evidence Comparison", to: "/KnowledgeLaboratoryPage#evidence-comparison" },
      { label: "Knowledge Laboratory", to: "/KnowledgeLaboratoryPage" },
      { label: "Claim Analyzer", to: "/CriticalThinkingPage#claim-analyzer" },
    ],
    status: "empirical",
    category: "epistemology",
  },
  {
    id: "expertise",
    term: "Expertise",
    definition: "High-level specialized domain competence, methodological training, and empirical mastery recognized through peer evaluation, credentials, and sustained track records.",
    example: "Consulting peer-reviewed virology research when assessing the transmission dynamics of an airborne respiratory pathogen.",
    related: ["testimony", "primary-source", "appeal-to-irrelevant-authority"],
    seeAlso: [
      { label: "Claim Analyzer", to: "/CriticalThinkingPage#claim-analyzer" },
      { label: "Critical Thinking Workshop", to: "/CriticalThinkingPage" },
    ],
    status: "empirical",
    category: "epistemology",
  },
  {
    id: "externalism",
    term: "Externalism",
    definition: "The epistemological thesis that factors external to an agent's conscious awareness—such as reliable causal cognitive mechanisms—determine whether a belief is justified or counts as knowledge.",
    example: "A person's true belief that a tree is outside is justified because their visual cortex functions reliably in broad daylight, even if they cannot articulate cognitive neuroscience.",
    related: ["internalism", "reliabilism", "justification", "knowledge"],
    seeAlso: [
      { label: "Knowledge Laboratory", to: "/KnowledgeLaboratoryPage" },
    ],
    status: "philosophical",
    category: "epistemology",
  },
  {
    id: "fallacy",
    term: "Fallacy",
    definition: "A defect or flaw in reasoning that impairs the logical validity or evidentiary soundness of an argument, rendering its conclusion unearned.",
    example: "Dismissing an opponent's statistical findings because you dislike their demeanor is a fallacious move.",
    related: ["formal-fallacy", "informal-fallacy", "argument", "invalid"],
    seeAlso: [
      { label: "Fallacy Library", to: "/FallacyLibraryPage" },
      { label: "Critical Thinking Workshop", to: "/CriticalThinkingPage" },
    ],
    status: "philosophical",
    category: "reasoning",
  },
  {
    id: "fallibilism",
    term: "Fallibilism",
    definition: "The epistemological principle that empirical knowledge can never be claimed with absolute, Cartesian certainty, and that any of our beliefs could conceivably turn out to be mistaken.",
    example: "Accepting general relativity as our best current description of gravitation while recognizing that future quantum gravity discoveries may revise it.",
    related: ["certainty", "skepticism", "epistemology", "falsifiability"],
    seeAlso: [
      { label: "Knowledge Laboratory", to: "/KnowledgeLaboratoryPage" },
    ],
    status: "philosophical",
    category: "epistemology",
  },
  {
    id: "false-dilemma",
    term: "False Dilemma",
    definition: "An informal fallacy that artificially constrains a complex spectrum of possibilities into only two mutually exclusive extremes, ignoring viable middle alternatives.",
    example: "'Either you support this unvetted budget allocation 100%, or you want our scientific department to collapse entirely.'",
    related: ["informal-fallacy", "fallacy", "disjunction"],
    seeAlso: [
      { label: "Fallacy: False Dilemma", to: "/FallacyLibraryPage#fallacy-false-dilemma" },
      { label: "Critical Thinking Workshop", to: "/CriticalThinkingPage" },
    ],
    status: "philosophical",
    category: "reasoning",
  },
  {
    id: "falsifiability",
    term: "Falsifiability",
    definition: "The methodological requirement formulated by Karl Popper that an empirical scientific hypothesis must make testable predictions capable of being shown false by observation.",
    example: "The claim 'all massive bodies attract each other inversely proportional to the square of their distance' is falsifiable because an observed violation would refute it.",
    related: ["hypothesis", "theory", "empiricism", "counterexample"],
    seeAlso: [
      { label: "Philosophy as Investigation", to: "/PhilosophyAsInvestigationPage" },
      { label: "Critical Thinking Workshop", to: "/CriticalThinkingPage" },
    ],
    status: "theory",
    category: "science",
  },
  {
    id: "formal-fallacy",
    term: "Formal Fallacy",
    definition: "A defect in the structural form or syntax of a deductive argument that renders the inference invalid, regardless of whether the premises happen to be factually true.",
    example: "Affirming the consequent: If P then Q; Q; therefore P.",
    related: ["informal-fallacy", "fallacy", "valid", "invalid", "deduction"],
    seeAlso: [
      { label: "Logic Laboratory", to: "/LogicLaboratoryPage" },
      { label: "Argument Tester", to: "/LogicLaboratoryPage#argument-tester" },
      { label: "Fallacy Library", to: "/FallacyLibraryPage" },
    ],
    status: "logical",
    category: "logic",
  },
  {
    id: "foundationalism",
    term: "Foundationalism",
    definition: "The epistemic doctrine that all knowledge and justified beliefs ultimately rest upon basic beliefs that are self-justifying or directly warranted without needing further inference.",
    example: "Descartes' pursuit of an unshakable foundation in the certainty of his own conscious experience.",
    related: ["coherentism", "infinitism", "justification", "knowledge"],
    seeAlso: [
      { label: "Knowledge Laboratory", to: "/KnowledgeLaboratoryPage" },
    ],
    status: "philosophical",
    category: "epistemology",
  },
  {
    id: "gamblers-fallacy",
    term: "Gambler's Fallacy",
    definition: "The mistaken belief that past independent random outcomes influence the probability of the next independent random trial in a Bernoulli sequence.",
    example: "Believing that after a fair coin lands on tails seven times consecutively, the eighth flip is 'due' to be heads.",
    related: ["cognitive-bias", "uncertainty", "fallacy"],
    seeAlso: [
      { label: "Fallacy: Gambler's Fallacy", to: "/FallacyLibraryPage#fallacy-gamblers-fallacy" },
    ],
    status: "logical",
    category: "reasoning",
  },
  {
    id: "genetic-fallacy",
    term: "Genetic Fallacy",
    definition: "Evaluating the truth or validity of an assertion solely by examining its historical origin, causal lineage, or how it arose, rather than testing its independent justification.",
    example: "Arguing that modern chemistry is bogus because its historical ancestor was medieval alchemy seeking to turn lead into gold.",
    related: ["ad-hominem", "informal-fallacy", "fallacy"],
    seeAlso: [
      { label: "Fallacy: Genetic Fallacy", to: "/FallacyLibraryPage#fallacy-genetic-fallacy" },
    ],
    status: "philosophical",
    category: "reasoning",
  },
  {
    id: "gettier-case",
    term: "Gettier Case",
    definition: "A philosophical thought experiment demonstrating that justified true belief (JTB) is not sufficient for knowledge, because luck or epistemic coincidence can connect the belief to the truth.",
    example: "Smith justifiedly believes someone in his office owns a Ford (because Nogot showed documents), but unbeknownst to Smith, Nogot was faking, yet Havit secretly owns a Ford.",
    related: ["justified-true-belief", "knowledge", "justification", "thought-experiment"],
    seeAlso: [
      { label: "Knowledge Laboratory", to: "/KnowledgeLaboratoryPage" },
      { label: "Evidence Comparison", to: "/KnowledgeLaboratoryPage#evidence-comparison" },
      { label: "Revise the Definition", to: "/PhilosophyAsInvestigationPage#revise-the-definition" },
    ],
    status: "philosophical",
    category: "epistemology",
  },
  {
    id: "hasty-generalization",
    term: "Hasty Generalization",
    definition: "Drawing a sweeping universal conclusion about an entire population based on an unrepresentative, biased, or tiny sample size.",
    example: "Interviewing two commuters at 6:00 AM and concluding that all residents of the metropolitan region wake up before dawn.",
    related: ["anecdotal-evidence", "misleading-sample", "induction", "fallacy"],
    seeAlso: [
      { label: "Fallacy: Hasty Generalization", to: "/FallacyLibraryPage#fallacy-hasty-generalization" },
      { label: "Critical Thinking Workshop", to: "/CriticalThinkingPage" },
    ],
    status: "unsupported",
    category: "reasoning",
  },
  {
    id: "hypothesis",
    term: "Hypothesis",
    definition: "A proposed, provisional explanation or testable conjecture for an observed phenomenon, offered as a starting point for further empirical investigation.",
    example: "Formulating the hypothesis that introducing an iron catalyst will decrease the activation energy of the ammonia synthesis reaction.",
    related: ["theory", "falsifiability", "model", "evidence"],
    seeAlso: [
      { label: "Critical Thinking Workshop", to: "/CriticalThinkingPage" },
      { label: "Claim Analyzer", to: "/CriticalThinkingPage#claim-analyzer" },
    ],
    status: "theory",
    category: "science",
  },
  {
    id: "hypothetical-syllogism",
    term: "Hypothetical Syllogism",
    definition: "A valid deductive inference rule linking conditional statements in a transitive chain: If P then Q; If Q then R; Therefore, If P then R.",
    example: "If it freezes, water turns to ice. If water turns to ice, it expands. Therefore, if it freezes, water expands.",
    related: ["conditional", "syllogism", "deduction", "valid"],
    seeAlso: [
      { label: "Logic Laboratory", to: "/LogicLaboratoryPage" },
      { label: "Argument Tester", to: "/LogicLaboratoryPage#argument-tester" },
    ],
    status: "logical",
    category: "logic",
  },
  {
    id: "idealism",
    term: "Idealism",
    definition: "The metaphysical thesis that reality, or reality as we know it, is fundamentally mental, immaterial, or constituted by ideas, minds, or perceptions.",
    example: "George Berkeley's subjective idealism, summarized as 'esse est percipi' (to be is to be perceived).",
    related: ["realism", "physicalism", "dualism", "metaphysics"],
    seeAlso: [
      { label: "Philosophy as Investigation", to: "/PhilosophyAsInvestigationPage" },
    ],
    status: "philosophical",
    category: "philosophy",
  },
  {
    id: "ignoring-intervals",
    term: "Ignoring Intervals",
    definition: "The error of treating a statistical estimate as a certain scalar number while neglecting the margin of error or confidence intervals accompanying it.",
    example: "Reporting that Candidate A (49% ± 3%) is definitively beating Candidate B (48% ± 3%), despite the confidence intervals overlapping substantially.",
    related: ["estimate-as-exact", "uncertainty", "misleading-sample"],
    seeAlso: [
      { label: "Fallacy Library", to: "/FallacyLibraryPage" },
      { label: "Critical Thinking Workshop", to: "/CriticalThinkingPage" },
    ],
    status: "unsupported",
    category: "reasoning",
  },
  {
    id: "incorrect-conditional-inference",
    term: "Incorrect Conditional Inference",
    definition: "An umbrella term for invalid deductive moves on conditional propositions, including affirming the consequent and denying the antecedent.",
    example: "Treating 'If it rains, the grass is wet' as if it automatically proved 'If the grass is wet, it must have rained.'",
    related: ["affirming-the-consequent", "denying-the-antecedent", "formal-fallacy"],
    seeAlso: [
      { label: "Logic Laboratory", to: "/LogicLaboratoryPage" },
      { label: "Argument Tester", to: "/LogicLaboratoryPage#argument-tester" },
    ],
    status: "logical",
    category: "logic",
  },
  {
    id: "induction",
    term: "Induction",
    definition: "A mode of reasoning that draws probabilistic, generalized conclusions from finite sets of specific observations, where true premises render the conclusion probable but not logically certain.",
    example: "Observing 10,000 sunrises and inferring that the sun will probabilistically rise tomorrow.",
    related: ["deduction", "abduction", "empiricism", "evidence"],
    seeAlso: [
      { label: "Critical Thinking Workshop", to: "/CriticalThinkingPage" },
      { label: "Knowledge Laboratory", to: "/KnowledgeLaboratoryPage" },
    ],
    status: "empirical",
    category: "reasoning",
  },
  {
    id: "inference-to-the-best-explanation",
    term: "Inference to the Best Explanation",
    definition: "An abductive principle that licenses accepting a hypothesis if it provides a superior, simpler, and more coherent explanatory account of observed phenomena than rival hypotheses.",
    example: "Geologists inferring continental drift because plate tectonics elegantly accounts for fossil distributions, mountain ranges, and earthquakes.",
    related: ["abduction", "theory", "hypothesis", "empiricism"],
    seeAlso: [
      { label: "Critical Thinking Workshop", to: "/CriticalThinkingPage" },
      { label: "Claim Analyzer", to: "/CriticalThinkingPage#claim-analyzer" },
    ],
    status: "philosophical",
    category: "reasoning",
  },
  {
    id: "infinitism",
    term: "Infinitism",
    definition: "The epistemological theory that justification is an infinite, non-repeating chain of reasons, rejecting both foundational stopping points and circular webs.",
    example: "An epistemic model where every justifying reason must itself be supported by a further distinct, unrepeated reason ad infinitum.",
    related: ["foundationalism", "coherentism", "justification"],
    seeAlso: [
      { label: "Knowledge Laboratory", to: "/KnowledgeLaboratoryPage" },
    ],
    status: "philosophical",
    category: "epistemology",
  },
  {
    id: "informal-fallacy",
    term: "Informal Fallacy",
    definition: "A defect in reasoning that depends on the substantive content, context, or semantics of an argument rather than its formal deductive structure.",
    example: "Committing a straw-man fallacy by exaggerating an opponent's environmental policy to make it easy to ridicule.",
    related: ["formal-fallacy", "fallacy", "argument", "straw-man"],
    seeAlso: [
      { label: "Fallacy Library", to: "/FallacyLibraryPage" },
      { label: "Critical Thinking Workshop", to: "/CriticalThinkingPage" },
    ],
    status: "philosophical",
    category: "reasoning",
  },
  {
    id: "internalism",
    term: "Internalism",
    definition: "The epistemological view that the factors determining the justification of a belief must be cognitively internal and accessible to the believer's conscious reflection.",
    example: "The requirement that for a belief to be justified, the thinker must consciously grasp the justifying reasons or evidence supporting it.",
    related: ["externalism", "justification", "knowledge", "epistemology"],
    seeAlso: [
      { label: "Knowledge Laboratory", to: "/KnowledgeLaboratoryPage" },
    ],
    status: "philosophical",
    category: "epistemology",
  },
  {
    id: "invalid",
    term: "Invalid",
    definition: "The property of a deductive argument whose structural form fails to guarantee that if all its premises are true, its conclusion must also be true.",
    example: "'All trees have leaves. My houseplant has leaves. Therefore, my houseplant is a tree.' (Invalid categorical structure).",
    related: ["valid", "formal-fallacy", "argument", "deduction"],
    seeAlso: [
      { label: "Logic Laboratory", to: "/LogicLaboratoryPage" },
      { label: "Argument Tester", to: "/LogicLaboratoryPage#argument-tester" },
    ],
    status: "logical",
    category: "logic",
  },
  {
    id: "invalid-conversion",
    term: "Invalid Conversion",
    definition: "A formal fallacy in term logic where the subject and predicate of a universal affirmative proposition are swapped without qualification: converting 'All S is P' into 'All P is S'.",
    example: "Converting 'All golden retrievers are dogs' into 'All dogs are golden retrievers.'",
    related: ["formal-fallacy", "invalid", "syllogism"],
    seeAlso: [
      { label: "Logic Laboratory", to: "/LogicLaboratoryPage" },
      { label: "Argument Tester", to: "/LogicLaboratoryPage#argument-tester" },
    ],
    status: "logical",
    category: "logic",
  },
  {
    id: "justification",
    term: "Justification",
    definition: "The rational ground, evidence, argument, or reliable cognitive warrant that confers epistemic legitimacy on a belief.",
    example: "Having clear laboratory logs, replicated readings, and rigorous calibration provides justification for believing a new reaction temperature.",
    related: ["knowledge", "belief", "truth", "evidence", "justified-true-belief"],
    seeAlso: [
      { label: "Knowledge Laboratory", to: "/KnowledgeLaboratoryPage" },
      { label: "Evidence Comparison", to: "/KnowledgeLaboratoryPage#evidence-comparison" },
    ],
    status: "philosophical",
    category: "epistemology",
  },
  {
    id: "justified-true-belief",
    term: "Justified True Belief (JTB)",
    definition: "The traditional tripartite definition of propositional knowledge dating to Plato, holding that an agent knows that P if and only if P is true, the agent believes P, and the agent is justified in believing P.",
    example: "You know Paris is the capital of France because it is factually true, you believe it, and you hold legitimate geographic justification for that belief.",
    related: ["knowledge", "belief", "justification", "truth", "gettier-case"],
    seeAlso: [
      { label: "Knowledge Laboratory", to: "/KnowledgeLaboratoryPage" },
      { label: "Revise the Definition", to: "/PhilosophyAsInvestigationPage#revise-the-definition" },
    ],
    status: "philosophical",
    category: "epistemology",
  },
  {
    id: "knowledge",
    term: "Knowledge",
    definition: "Cognitive grasp of objective reality characterized by truth, adequate epistemic justification or warrant, and immunity to Gettier-style epistemic accidents.",
    example: "Grasping that water molecules consist of two hydrogen atoms bonded to one oxygen atom through verified molecular spectroscopy.",
    related: ["justified-true-belief", "belief", "truth", "justification", "epistemology"],
    seeAlso: [
      { label: "Knowledge Laboratory", to: "/KnowledgeLaboratoryPage" },
      { label: "Evidence Comparison", to: "/KnowledgeLaboratoryPage#evidence-comparison" },
    ],
    status: "philosophical",
    category: "epistemology",
  },
  {
    id: "loaded-question",
    term: "Loaded Question",
    definition: "An informal fallacy consisting of a question containing an unjustified, unproven presumption of guilt or fact built into its presupposition.",
    example: "'Have you finally stopped falsifying your research data?' (Answering yes or no concedes the prior falsification).",
    related: ["informal-fallacy", "begging-the-question", "misleading-framing"],
    seeAlso: [
      { label: "Fallacy: Loaded Question", to: "/FallacyLibraryPage#fallacy-loaded-question" },
      { label: "Critical Thinking Workshop", to: "/CriticalThinkingPage" },
    ],
    status: "philosophical",
    category: "reasoning",
  },
  {
    id: "logical-equivalence",
    term: "Logical Equivalence",
    definition: "The relationship between two propositions that necessarily share the exact same truth value under all possible truth-value assignments.",
    example: "The conditional 'P → Q' is logically equivalent to its contrapositive '¬Q → ¬P'.",
    related: ["tautology", "biconditional", "modus-tollens", "valid"],
    seeAlso: [
      { label: "Truth Table Tool", to: "/LogicLaboratoryPage#truth-table" },
      { label: "Logic Laboratory", to: "/LogicLaboratoryPage" },
    ],
    status: "logical",
    category: "logic",
  },
  {
    id: "metaphysics",
    term: "Metaphysics",
    definition: "The branch of philosophy that investigates the fundamental nature of reality, existence, identity, causation, time, space, and being.",
    example: "Debating whether abstract objects such as mathematical numbers possess real metaphysical existence independent of human thought.",
    related: ["ontology", "realism", "idealism", "physicalism", "epistemology"],
    seeAlso: [
      { label: "Philosophy as Investigation", to: "/PhilosophyAsInvestigationPage" },
      { label: "Simulation Theory", to: "/SimulationTheoryPage" },
    ],
    status: "philosophical",
    category: "philosophy",
  },
  {
    id: "misinformation",
    term: "Misinformation",
    definition: "False, inaccurate, or misleading information communicated regardless of whether there is deliberate intent to deceive.",
    example: "Sharing an outdated, uncorrected map of atmospheric storm tracks that inadvertently misleads coastal residents.",
    related: ["evidence", "primary-source", "skepticism", "uncertainty"],
    seeAlso: [
      { label: "Critical Thinking Workshop", to: "/CriticalThinkingPage" },
      { label: "Claim Analyzer", to: "/CriticalThinkingPage#claim-analyzer" },
    ],
    status: "unsupported",
    category: "reasoning",
  },
  {
    id: "misleading-framing",
    term: "Misleading Framing",
    definition: "Structuring how a situation, statistic, or argument is verbally presented to evoke a biased conclusion while omitting crucial context.",
    example: "Advertising a surgical procedure as having 'a 90% survival rate' while omitting that untreated patients have a 98% survival rate.",
    related: ["relative-vs-absolute-risk", "informal-fallacy", "cherry-picking"],
    seeAlso: [
      { label: "Fallacy Library", to: "/FallacyLibraryPage" },
      { label: "Critical Thinking Workshop", to: "/CriticalThinkingPage" },
    ],
    status: "philosophical",
    category: "reasoning",
  },
  {
    id: "misleading-sample",
    term: "Misleading Sample",
    definition: "Using a data sample that is systematically unrepresentative of the target population to infer a general claim.",
    example: "Surveying customer sentiment about high-speed internet only among attendees at an exclusive luxury electronics gala.",
    related: ["hasty-generalization", "survivorship-bias", "empirical"],
    seeAlso: [
      { label: "Fallacy Library", to: "/FallacyLibraryPage" },
      { label: "Critical Thinking Workshop", to: "/CriticalThinkingPage" },
    ],
    status: "unsupported",
    category: "reasoning",
  },
  {
    id: "misreading-significance",
    term: "Misreading Significance",
    definition: "Conflating statistical significance (p < 0.05) with substantive, practical, or clinical importance in the physical world.",
    example: "Claiming an educational breakthrough because a massive sample showed a 0.2% test score improvement with p = 0.001.",
    related: ["no-significance-no-effect", "empirical", "uncertainty"],
    seeAlso: [
      { label: "Fallacy Library", to: "/FallacyLibraryPage" },
    ],
    status: "empirical",
    category: "science",
  },
  {
    id: "model",
    term: "Scientific Model",
    definition: "A conceptual, mathematical, or computational representation of an empirical system designed to simulate its behavior and generate testable predictions.",
    example: "The standard cosmological model (Lambda-CDM) representing cosmic expansion and galactic structure formation.",
    related: ["theory", "hypothesis", "falsifiability", "empirical"],
    seeAlso: [
      { label: "Simulation Theory", to: "/SimulationTheoryPage" },
      { label: "Critical Thinking Workshop", to: "/CriticalThinkingPage" },
    ],
    status: "theory",
    category: "science",
  },
  {
    id: "modus-ponens",
    term: "Modus Ponens",
    definition: "The fundamental valid deductive rule of inference asserting that if a conditional statement is true and its antecedent is true, its consequent must be true: If P then Q; P; therefore Q.",
    example: "If a compound contains carbon, it is organic. This sample contains carbon. Therefore, this sample is organic.",
    related: ["conditional", "modus-tollens", "valid", "deduction"],
    seeAlso: [
      { label: "Logic Laboratory", to: "/LogicLaboratoryPage" },
      { label: "Argument Tester", to: "/LogicLaboratoryPage#argument-tester" },
    ],
    status: "logical",
    category: "logic",
  },
  {
    id: "modus-tollens",
    term: "Modus Tollens",
    definition: "The valid deductive rule of inference asserting that if a conditional statement is true and its consequent is false, its antecedent must also be false: If P then Q; Not Q; therefore Not P.",
    example: "If the reactor had breached, radiation alarms would be sounding. Radiation alarms are silent. Therefore, the reactor has not breached.",
    related: ["conditional", "modus-ponens", "valid", "deduction"],
    seeAlso: [
      { label: "Logic Laboratory", to: "/LogicLaboratoryPage" },
      { label: "Argument Tester", to: "/LogicLaboratoryPage#argument-tester" },
    ],
    status: "logical",
    category: "logic",
  },
  {
    id: "moving-the-goalposts",
    term: "Moving the Goalposts",
    definition: "An informal fallacy where an arguer arbitrarily changes the accepted criteria of proof or evidence as soon as the initial criteria have been successfully satisfied.",
    example: "Demanding a fossil showing transition between land mammals and whales, and upon being shown it, demanding two new transitional fossils bridging the new gap.",
    related: ["informal-fallacy", "special-pleading", "fallacy"],
    seeAlso: [
      { label: "Fallacy: Moving the Goalposts", to: "/FallacyLibraryPage#fallacy-moving-the-goalposts" },
      { label: "Critical Thinking Workshop", to: "/CriticalThinkingPage" },
    ],
    status: "philosophical",
    category: "reasoning",
  },
  {
    id: "necessary-condition",
    term: "Necessary Condition",
    definition: "A requirement that must be satisfied for a condition or statement to hold true; without it, the condition cannot occur.",
    example: "Having oxygen is a necessary condition for open terrestrial combustion (without oxygen, wood fire cannot burn).",
    related: ["sufficient-condition", "conditional", "biconditional", "conceptual-analysis"],
    seeAlso: [
      { label: "Philosophy as Investigation", to: "/PhilosophyAsInvestigationPage" },
      { label: "Revise the Definition", to: "/PhilosophyAsInvestigationPage#revise-the-definition" },
      { label: "Logic Laboratory", to: "/LogicLaboratoryPage" },
    ],
    status: "logical",
    category: "logic",
  },
  {
    id: "negation",
    term: "Negation",
    definition: "The unary logical operator ('not', denoted ¬P) that inverts the truth value of a proposition, turning true into false and false into true.",
    example: "If P is 'The photon has zero rest mass' (true), then ¬P is 'The photon has non-zero rest mass' (false).",
    related: ["contradiction", "conjunction", "disjunction"],
    seeAlso: [
      { label: "Truth Table Tool", to: "/LogicLaboratoryPage#truth-table" },
      { label: "Logic Laboratory", to: "/LogicLaboratoryPage" },
    ],
    status: "logical",
    category: "logic",
  },
  {
    id: "no-significance-no-effect",
    term: "No Significance Is Not No Effect",
    definition: "The error of treating an underpowered study that failed to reach statistical significance (p > 0.05) as definitive proof that the phenomenon does not exist.",
    example: "Testing a new antiviral on only six patients, observing a 20% benefit that wasn't statistically significant, and concluding the drug has zero efficacy.",
    related: ["absence-of-evidence", "misreading-significance", "empirical"],
    seeAlso: [
      { label: "Fallacy Library", to: "/FallacyLibraryPage" },
    ],
    status: "empirical",
    category: "science",
  },
  {
    id: "no-true-scotsman",
    term: "No True Scotsman",
    definition: "An informal fallacy where an arguer protects a universal generalization from a clear counterexample by arbitrarily redefining membership in the category to exclude the case.",
    example: "'No real philosopher would ever doubt logic.' When shown a philosopher who does: 'Well, they aren't a true philosopher.'",
    related: ["special-pleading", "ambiguous-definition", "informal-fallacy"],
    seeAlso: [
      { label: "Fallacy: No True Scotsman", to: "/FallacyLibraryPage#fallacy-no-true-scotsman" },
      { label: "Philosophy as Investigation", to: "/PhilosophyAsInvestigationPage" },
    ],
    status: "philosophical",
    category: "reasoning",
  },
  {
    id: "normative-claim",
    term: "Normative Claim",
    definition: "A statement asserting how things ought to be, what is good or bad, or which actions are morally obligatory, rather than merely describing what is.",
    example: "'Societies ought to distribute resources to minimize extreme suffering.'",
    related: ["descriptive-claim", "conceptual-analysis"],
    seeAlso: [
      { label: "Critical Thinking Workshop", to: "/CriticalThinkingPage" },
      { label: "Claim Analyzer", to: "/CriticalThinkingPage#claim-analyzer" },
    ],
    status: "philosophical",
    category: "philosophy",
  },
  {
    id: "ontology",
    term: "Ontology",
    definition: "The subfield of metaphysics focused specifically on the taxonomy of being, studying what categories of entities fundamentally exist.",
    example: "Debating whether conscious minds are distinct substances or physical configurations of matter.",
    related: ["metaphysics", "physicalism", "dualism", "realism"],
    seeAlso: [
      { label: "Philosophy as Investigation", to: "/PhilosophyAsInvestigationPage" },
    ],
    status: "philosophical",
    category: "philosophy",
  },
  {
    id: "oversimplified-cause",
    term: "Oversimplified Cause",
    definition: "Attributing an outcome driven by complex multifactorial causes to a single isolated variable.",
    example: "Claiming that the global economic recession of 2008 was caused entirely by one bank teller's mistake.",
    related: ["causation", "confounding", "post-hoc"],
    seeAlso: [
      { label: "Fallacy Library", to: "/FallacyLibraryPage" },
      { label: "Critical Thinking Workshop", to: "/CriticalThinkingPage" },
    ],
    status: "unsupported",
    category: "reasoning",
  },
  {
    id: "paradox",
    term: "Paradox",
    definition: "An apparently sound argument originating from seemingly true premises that leads to a conclusion that is self-contradictory or completely unacceptable.",
    example: "The Liar Paradox: 'This statement is false', which cannot be true without being false, nor false without being true.",
    related: ["contradiction", "thought-experiment", "valid", "consistency"],
    seeAlso: [
      { label: "Philosophy as Investigation", to: "/PhilosophyAsInvestigationPage" },
      { label: "Logic Laboratory", to: "/LogicLaboratoryPage" },
    ],
    status: "philosophical",
    category: "philosophy",
  },
  {
    id: "physicalism",
    term: "Physicalism",
    definition: "The metaphysical thesis that everything in reality is ultimately physical, and that all mental, biological, and social properties supervene on fundamental physical processes.",
    example: "The view that conscious human experience is identical to or supervenient upon neurobiological activity in the brain.",
    related: ["dualism", "idealism", "metaphysics", "ontology"],
    seeAlso: [
      { label: "Philosophy as Investigation", to: "/PhilosophyAsInvestigationPage" },
      { label: "Simulation Theory", to: "/SimulationTheoryPage" },
    ],
    status: "philosophical",
    category: "philosophy",
  },
  {
    id: "post-hoc",
    term: "Post Hoc Ergo Propter Hoc",
    definition: "The informal causal fallacy of inferring that because event Y occurred immediately after event X, event X must have caused event Y.",
    example: "A rooster crows before sunrise every dawn; inferring that the rooster's crow causes the sun to rise.",
    related: ["causation", "correlation", "confounding", "fallacy"],
    seeAlso: [
      { label: "Fallacy: Post Hoc", to: "/FallacyLibraryPage#fallacy-post-hoc" },
      { label: "Critical Thinking Workshop", to: "/CriticalThinkingPage" },
    ],
    status: "philosophical",
    category: "reasoning",
  },
  {
    id: "premise",
    term: "Premise",
    definition: "A proposition in an argument offered as foundational support or evidence from which a conclusion is inferred.",
    example: "In 'All men are mortal; Socrates is a man; therefore Socrates is mortal', the first two statements are premises.",
    related: ["conclusion", "argument", "assumption", "valid"],
    seeAlso: [
      { label: "Critical Thinking Workshop", to: "/CriticalThinkingPage" },
      { label: "Logic Laboratory", to: "/LogicLaboratoryPage" },
      { label: "Argument Tester", to: "/LogicLaboratoryPage#argument-tester" },
    ],
    status: "logical",
    category: "logic",
  },
  {
    id: "primary-source",
    term: "Primary Source",
    definition: "An immediate, first-hand artifact, recorded empirical dataset, or original text created at the time under study by direct participants.",
    example: "A laboratory notebook recording raw telescope photon detector outputs during a supernova event.",
    related: ["testimony", "expertise", "evidence"],
    seeAlso: [
      { label: "Glossary & References", to: "/GlossaryAndReferencesPage" },
      { label: "Critical Thinking Workshop", to: "/CriticalThinkingPage" },
    ],
    status: "empirical",
    category: "epistemology",
  },
  {
    id: "principle-of-charity",
    term: "Principle of Charity",
    definition: "The methodological norm requiring an analyst to interpret an interlocutor's argument in its strongest, most coherent, and most rational form before critiquing it.",
    example: "Fixing an accidental verbal slip in an opponent's premise rather than seizing upon the typo to dismiss their entire mathematical proof.",
    related: ["steelmanning", "straw-man", "argument"],
    seeAlso: [
      { label: "Critical Thinking Workshop", to: "/CriticalThinkingPage" },
      { label: "Claim Analyzer", to: "/CriticalThinkingPage#claim-analyzer" },
    ],
    status: "philosophical",
    category: "reasoning",
  },
  {
    id: "prosecutors-fallacy",
    term: "Prosecutor's Fallacy",
    definition: "A fallacy of statistical reasoning where the probability of a match given innocence P(Match|Innocent) is conflated with the probability of innocence given a match P(Innocent|Match).",
    example: "Arguing that because a DNA match occurs in only 1 in 1,000,000 innocent people, a suspect found in a national database of 10,000,000 has a 99.9999% chance of guilt.",
    related: ["conditional-probability-confusion", "base-rate-neglect", "fallacy"],
    seeAlso: [
      { label: "Fallacy: Prosecutor's Fallacy", to: "/FallacyLibraryPage#fallacy-prosecutors-fallacy" },
    ],
    status: "logical",
    category: "reasoning",
  },
  {
    id: "quantifier",
    term: "Quantifier",
    definition: "A logical operator that specifies the quantity of specimens in the domain of discourse that satisfy a given open predicate, primarily universal (∀, 'for all') and existential (∃, 'there exists').",
    example: "The proposition '∀x (Human(x) → Mortal(x))' asserts that for every entity x, if x is human, x is mortal.",
    related: ["deduction", "syllogism", "valid"],
    seeAlso: [
      { label: "Logic Laboratory", to: "/LogicLaboratoryPage" },
      { label: "Argument Tester", to: "/LogicLaboratoryPage#argument-tester" },
    ],
    status: "logical",
    category: "logic",
  },
  {
    id: "rationalism",
    term: "Rationalism",
    definition: "The epistemological theory asserting that reason, deduction, and innate intellectual intuition are the primary sources and tests of knowledge, distinct from sensory perception.",
    example: "Spinoza's geometric deduction of metaphysical principles in his Ethics.",
    related: ["empiricism", "a-priori", "deduction"],
    seeAlso: [
      { label: "Philosophy as Investigation", to: "/PhilosophyAsInvestigationPage" },
      { label: "Knowledge Laboratory", to: "/KnowledgeLaboratoryPage" },
    ],
    status: "philosophical",
    category: "epistemology",
  },
  {
    id: "realism",
    term: "Realism",
    definition: "The metaphysical and epistemological view that reality exists independently of our conceptual frameworks, beliefs, perceptions, or linguistic descriptions.",
    example: "Scientific realism asserts that unobservable entities postulated by our best theories (such as quarks and black holes) genuinely exist in nature.",
    related: ["idealism", "metaphysics", "ontology", "physicalism"],
    seeAlso: [
      { label: "Philosophy as Investigation", to: "/PhilosophyAsInvestigationPage" },
      { label: "Simulation Theory", to: "/SimulationTheoryPage" },
    ],
    status: "philosophical",
    category: "philosophy",
  },
  {
    id: "red-herring",
    term: "Red Herring",
    definition: "An informal fallacy where an arguer introduces an irrelevant, distracting topic into a discussion to divert attention away from the core issue.",
    example: "When asked about missing laboratory research funds, an administrator responds with an impassioned speech praising campus landscaping.",
    related: ["straw-man", "informal-fallacy", "fallacy"],
    seeAlso: [
      { label: "Fallacy: Red Herring", to: "/FallacyLibraryPage#fallacy-red-herring" },
      { label: "Critical Thinking Workshop", to: "/CriticalThinkingPage" },
    ],
    status: "philosophical",
    category: "reasoning",
  },
  {
    id: "reductio-ad-absurdum",
    term: "Reductio ad Absurdum",
    definition: "A valid form of deductive refutation that disproves a proposition by showing that assuming its truth leads inexorably to a logical contradiction or absurd consequence.",
    example: "Euclid's proof of the infinitude of prime numbers assumes there is a largest prime and derives an irreconcilable contradiction.",
    related: ["contradiction", "valid", "deduction"],
    seeAlso: [
      { label: "Logic Laboratory", to: "/LogicLaboratoryPage" },
      { label: "Philosophy as Investigation", to: "/PhilosophyAsInvestigationPage" },
    ],
    status: "logical",
    category: "logic",
  },
  {
    id: "reflective-equilibrium",
    term: "Reflective Equilibrium",
    definition: "A state of dynamic balance arrived at by deliberative mutual adjustment between our general principles and our considered judgments about specific cases.",
    example: "John Rawls' method in political philosophy of aligning intuitive judgments about fair distribution with general principles of justice.",
    related: ["conceptual-analysis", "thought-experiment", "coherentism"],
    seeAlso: [
      { label: "Philosophy as Investigation", to: "/PhilosophyAsInvestigationPage" },
    ],
    status: "philosophical",
    category: "philosophy",
  },
  {
    id: "regression-to-the-mean",
    term: "Regression to the Mean",
    definition: "The statistical phenomenon where extreme or unusual measurements are naturally followed by subsequent measurements that lie closer to the population average.",
    example: "A student who scores an unusually high 99% on an exam tends to score closer to their true average (say 85%) on the next exam, without needing any special intervention.",
    related: ["causation", "correlation", "confounding"],
    seeAlso: [
      { label: "Fallacy: Regression to the Mean", to: "/FallacyLibraryPage#fallacy-regression-to-the-mean" },
    ],
    status: "empirical",
    category: "science",
  },
  {
    id: "reification",
    term: "Reification (Hypostatization)",
    definition: "The fallacy of treating an abstract concept, metaphor, or heuristic model as if it were a concrete, physically existing entity with causal agency.",
    example: "Claiming that 'Mother Nature decided to punish the coastline with hurricanes' treats a poetic metaphor as an intentional physical agent.",
    related: ["informal-fallacy", "fallacy", "model"],
    seeAlso: [
      { label: "Fallacy Library", to: "/FallacyLibraryPage" },
    ],
    status: "philosophical",
    category: "reasoning",
  },
  {
    id: "relative-vs-absolute-risk",
    term: "Relative vs. Absolute Risk",
    definition: "The confusion or misleading framing that occurs when changes in probability are reported as dramatic relative percentages rather than modest absolute changes.",
    example: "Reporting that a treatment 'doubles your risk (100% increase)' when the baseline absolute risk rises from 1 in 100,000 to 2 in 100,000.",
    related: ["misleading-framing", "base-rate", "uncertainty"],
    seeAlso: [
      { label: "Fallacy Library", to: "/FallacyLibraryPage" },
      { label: "Critical Thinking Workshop", to: "/CriticalThinkingPage" },
    ],
    status: "empirical",
    category: "reasoning",
  },
  {
    id: "reliabilism",
    term: "Reliabilism",
    definition: "The externalist epistemological theory holding that a belief is justified if and only if it is generated by a cognitive process that reliably produces a high ratio of true beliefs.",
    example: "Visual perception in normal lighting is a reliable belief-forming mechanism, whereas wishful thinking is not.",
    related: ["externalism", "justification", "knowledge", "virtue-epistemology"],
    seeAlso: [
      { label: "Knowledge Laboratory", to: "/KnowledgeLaboratoryPage" },
    ],
    status: "philosophical",
    category: "epistemology",
  },
  {
    id: "reverse-causation",
    term: "Reverse Causation",
    definition: "The causal fallacy of inverting directionality between cause and effect, misinterpreting the consequence as the trigger.",
    example: "Observing that frequent hospital visits correlate with severe illness and concluding that hospitals make healthy people sick.",
    related: ["causation", "correlation", "confounding"],
    seeAlso: [
      { label: "Fallacy: Correlation vs Causation", to: "/FallacyLibraryPage#fallacy-correlation-causation" },
    ],
    status: "empirical",
    category: "science",
  },
  {
    id: "selective-reporting",
    term: "Selective Reporting",
    definition: "The practice of publishing or publicizing only the empirical trials or sub-analyses that yield statistically significant or desirable findings.",
    example: "The publication bias phenomenon (the 'file drawer problem') where 19 trials showing no drug efficacy remain unpublished while one lucky trial is printed.",
    related: ["cherry-picking", "confirmation-bias", "empirical"],
    seeAlso: [
      { label: "Fallacy: Cherry-Picking", to: "/FallacyLibraryPage#fallacy-cherry-picking" },
      { label: "Critical Thinking Workshop", to: "/CriticalThinkingPage" },
    ],
    status: "unsupported",
    category: "science",
  },
  {
    id: "simulation-argument",
    term: "Simulation Argument",
    definition: "Nick Bostrom's trilemma argument showing that at least one of three propositions is almost certainly true: humanity goes extinct before reaching posthuman technological capability, posthuman civilizations lose interest in running ancestor simulations, or we are almost certainly living in a simulation.",
    example: "If ancestor simulations are technologically feasible and routinely run by advanced intelligences, simulated minds vastly outnumber biological minds.",
    related: ["skepticism", "metaphysics", "thought-experiment", "model"],
    seeAlso: [
      { label: "Simulation Theory", to: "/SimulationTheoryPage" },
      { label: "Philosophy as Investigation", to: "/PhilosophyAsInvestigationPage" },
    ],
    status: "speculative",
    category: "philosophy",
  },
  {
    id: "skepticism",
    term: "Skepticism",
    definition: "The philosophical stance or inquiry questioning the certainty, reliability, or foundational grounds of knowledge claims.",
    example: "Methodological Cartesian skepticism, which systematically doubts every belief that admits of even the slightest conceivable error.",
    related: ["certainty", "fallibilism", "epistemology", "justification"],
    seeAlso: [
      { label: "Knowledge Laboratory", to: "/KnowledgeLaboratoryPage" },
      { label: "Philosophy as Investigation", to: "/PhilosophyAsInvestigationPage" },
      { label: "Simulation Theory", to: "/SimulationTheoryPage" },
    ],
    status: "philosophical",
    category: "epistemology",
  },
  {
    id: "slippery-slope",
    term: "Slippery Slope",
    definition: "An informal fallacy asserting that taking a relatively modest initial step will inevitably lead through an uncontrollable chain reaction to a catastrophic outcome, without establishing causal links.",
    example: "'If we permit students to retake a quiz, soon nobody will ever attend lectures and the entire university system will collapse into anarchy.'",
    related: ["informal-fallacy", "causation", "fallacy"],
    seeAlso: [
      { label: "Fallacy: Slippery Slope", to: "/FallacyLibraryPage#fallacy-slippery-slope" },
      { label: "Critical Thinking Workshop", to: "/CriticalThinkingPage" },
    ],
    status: "philosophical",
    category: "reasoning",
  },
  {
    id: "sound",
    term: "Sound",
    definition: "The property of a deductive argument that is both structurally valid AND whose premises are all factually true in the real world, guaranteeing a true conclusion.",
    example: "'All mammals have hearts. All blue whales are mammals. Blue whales have hearts.' (Valid deductive form with true premises).",
    related: ["valid", "invalid", "premise", "argument", "deduction"],
    seeAlso: [
      { label: "Logic Laboratory", to: "/LogicLaboratoryPage" },
      { label: "Argument Tester", to: "/LogicLaboratoryPage#argument-tester" },
      { label: "Critical Thinking Workshop", to: "/CriticalThinkingPage" },
    ],
    status: "logical",
    category: "logic",
  },
  {
    id: "special-pleading",
    term: "Special Pleading",
    definition: "An informal fallacy wherein an arguer applies a rigorous standard or rule to others while exempting themselves without providing a justifiable, non-arbitrary reason.",
    example: "'Everyone must submit to strict safety audits, except my laboratory, because our work is uniquely visionary.'",
    related: ["informal-fallacy", "fallacy", "no-true-scotsman"],
    seeAlso: [
      { label: "Fallacy: Special Pleading", to: "/FallacyLibraryPage#fallacy-special-pleading" },
      { label: "Critical Thinking Workshop", to: "/CriticalThinkingPage" },
    ],
    status: "philosophical",
    category: "reasoning",
  },
  {
    id: "steelmanning",
    term: "Steelmanning",
    definition: "The practice of reconstructing an opposing argument in its strongest, most sophisticated, and most defensible form before proceeding to evaluate its merits.",
    example: "Addressing the best empirical and statistical objections of an economic critic rather than their most trivial rhetorical quip.",
    related: ["principle-of-charity", "straw-man", "argument"],
    seeAlso: [
      { label: "Critical Thinking Workshop", to: "/CriticalThinkingPage" },
      { label: "Claim Analyzer", to: "/CriticalThinkingPage#claim-analyzer" },
    ],
    status: "philosophical",
    category: "reasoning",
  },
  {
    id: "straw-man",
    term: "Straw Man",
    definition: "An informal fallacy in which an opponent's actual argument is mischaracterized, exaggerated, or distorted into a weaker version that is easier to attack and refute.",
    example: "'Advocates for public transit simply want to ban all private automobiles and force everyone to ride bicycles in blizzards.'",
    related: ["principle-of-charity", "steelmanning", "red-herring", "fallacy"],
    seeAlso: [
      { label: "Fallacy: Straw Man", to: "/FallacyLibraryPage#fallacy-straw-man" },
      { label: "Critical Thinking Workshop", to: "/CriticalThinkingPage" },
    ],
    status: "philosophical",
    category: "reasoning",
  },
  {
    id: "sufficient-condition",
    term: "Sufficient Condition",
    definition: "A condition that, if satisfied, entirely guarantees the truth or occurrence of another event or statement.",
    example: "Being a blue whale is a sufficient condition for being a mammal (satisfying it guarantees mammal status).",
    related: ["necessary-condition", "conditional", "biconditional"],
    seeAlso: [
      { label: "Philosophy as Investigation", to: "/PhilosophyAsInvestigationPage" },
      { label: "Logic Laboratory", to: "/LogicLaboratoryPage" },
    ],
    status: "logical",
    category: "logic",
  },
  {
    id: "survivorship-bias",
    term: "Survivorship Bias",
    definition: "A logical and statistical error that focuses only on successful or surviving specimens that passed an ordeal while ignoring those that failed, skewing conclusions.",
    example: "Examining returning combat bombers to armor where bullet holes are densest, ignoring that aircraft shot in other areas did not survive to be examined.",
    related: ["cherry-picking", "misleading-sample", "cognitive-bias"],
    seeAlso: [
      { label: "Fallacy: Survivorship Bias", to: "/FallacyLibraryPage#fallacy-survivorship-bias" },
    ],
    status: "empirical",
    category: "reasoning",
  },
  {
    id: "syllogism",
    term: "Syllogism",
    definition: "A classic form of deductive argument comprising three propositions: a major premise, a minor premise, and a conclusion derived through term analysis.",
    example: "'All humans are mortal (major). Socrates is human (minor). Socrates is mortal (conclusion).'",
    related: ["deduction", "valid", "premise", "conclusion", "disjunctive-syllogism"],
    seeAlso: [
      { label: "Logic Laboratory", to: "/LogicLaboratoryPage" },
      { label: "Argument Tester", to: "/LogicLaboratoryPage#argument-tester" },
    ],
    status: "logical",
    category: "logic",
  },
  {
    id: "tautology",
    term: "Tautology",
    definition: "A compound proposition in formal logic that evaluates to true under every possible assignment of truth values to its constituent atomic variables.",
    example: "'P ∨ ¬P' (Either it is raining or it is not raining) is true under all possible circumstances.",
    related: ["contradiction", "contingency", "logical-equivalence"],
    seeAlso: [
      { label: "Truth Table Tool", to: "/LogicLaboratoryPage#truth-table" },
      { label: "Logic Laboratory", to: "/LogicLaboratoryPage" },
    ],
    status: "logical",
    category: "logic",
  },
  {
    id: "testimony",
    term: "Testimony",
    definition: "An epistemic source of belief and knowledge based on what other persons communicate, assert, or document.",
    example: "Learning the historical date of the Apollo 11 lunar landing from verified astronomical archives and astronaut logs.",
    related: ["expertise", "primary-source", "evidence", "epistemology"],
    seeAlso: [
      { label: "Knowledge Laboratory", to: "/KnowledgeLaboratoryPage" },
      { label: "Evidence Comparison", to: "/KnowledgeLaboratoryPage#evidence-comparison" },
      { label: "Critical Thinking Workshop", to: "/CriticalThinkingPage" },
    ],
    status: "philosophical",
    category: "epistemology",
  },
  {
    id: "texas-sharpshooter",
    term: "Texas Sharpshooter Fallacy",
    definition: "The fallacy of cherry-picking clusters of data or coincidences within random scatter and retrospectively drawing a bullseye target around them to claim meaning.",
    example: "Noticing that five people in a town of 100,000 contracted rare cancers in the same decade and claiming a local factory must be culpable before conducting a cluster analysis.",
    related: ["cherry-picking", "confirmation-bias", "fallacy"],
    seeAlso: [
      { label: "Fallacy: Texas Sharpshooter", to: "/FallacyLibraryPage#fallacy-texas-sharpshooter" },
    ],
    status: "unsupported",
    category: "reasoning",
  },
  {
    id: "theory",
    term: "Scientific Theory",
    definition: "A comprehensive, structured explanatory framework validated by extensive predictive accuracy, falsifiability testing, and widespread scientific consensus.",
    example: "The theory of quantum electrodynamics explaining interactions between photons and charged matter with extreme mathematical precision.",
    related: ["hypothesis", "model", "falsifiability", "evidence"],
    seeAlso: [
      { label: "Philosophy as Investigation", to: "/PhilosophyAsInvestigationPage" },
      { label: "Critical Thinking Workshop", to: "/CriticalThinkingPage" },
    ],
    status: "theory",
    category: "science",
  },
  {
    id: "thought-experiment",
    term: "Thought Experiment",
    definition: "An imaginative, controlled mental scenario designed to isolate conceptual principles, expose logical contradictions, or test the implications of a hypothesis.",
    example: "Einstein's gedankenexperiment of chasing a light beam, which stimulated the formulation of special relativity.",
    related: ["gettier-case", "conceptual-analysis", "reductio-ad-absurdum"],
    seeAlso: [
      { label: "Philosophy as Investigation", to: "/PhilosophyAsInvestigationPage" },
      { label: "Simulation Theory", to: "/SimulationTheoryPage" },
    ],
    status: "philosophical",
    category: "philosophy",
  },
  {
    id: "truth",
    term: "Truth",
    definition: "The property of a proposition corresponding to objective reality, state of affairs, or factual being.",
    example: "The proposition 'Gold has atomic number 79' is true because gold nuclei in physical reality contain exactly 79 protons.",
    related: ["belief", "justification", "knowledge", "justified-true-belief"],
    seeAlso: [
      { label: "Knowledge Laboratory", to: "/KnowledgeLaboratoryPage" },
      { label: "Philosophy as Investigation", to: "/PhilosophyAsInvestigationPage" },
    ],
    status: "philosophical",
    category: "epistemology",
  },
  {
    id: "tu-quoque",
    term: "Tu Quoque",
    definition: "A specific variant of the ad hominem fallacy that attempts to discredit an argument by pointing out that the speaker has acted inconsistently with their own claim.",
    example: "'Why should I listen to your advice on reducing carbon emissions when you just drove an SUV to this conference?'",
    related: ["ad-hominem", "informal-fallacy", "fallacy"],
    seeAlso: [
      { label: "Fallacy: Tu Quoque", to: "/FallacyLibraryPage#fallacy-tu-quoque" },
    ],
    status: "philosophical",
    category: "reasoning",
  },
  {
    id: "uncertainty",
    term: "Uncertainty",
    definition: "The state of lacking complete knowledge or exact predictability regarding outcomes, parameter measurements, or future events.",
    example: "Quantum mechanical Heisenberg uncertainty, where a particle's position and momentum cannot simultaneously be measured with arbitrary precision.",
    related: ["base-rate", "fallibilism", "ignoring-intervals"],
    seeAlso: [
      { label: "Critical Thinking Workshop", to: "/CriticalThinkingPage" },
      { label: "Simulation Theory", to: "/SimulationTheoryPage" },
    ],
    status: "theory",
    category: "science",
  },
  {
    id: "undistributed-middle",
    term: "Undistributed Middle",
    definition: "A formal syllogistic fallacy occurring when the middle term connecting the premises is not distributed in at least one premise.",
    example: "'All sharks are fish. All salmon are fish. Therefore, all sharks are salmon.' ('Fish' is undistributed in both premises).",
    related: ["formal-fallacy", "syllogism", "invalid"],
    seeAlso: [
      { label: "Logic Laboratory", to: "/LogicLaboratoryPage" },
      { label: "Argument Tester", to: "/LogicLaboratoryPage#argument-tester" },
      { label: "Fallacy: Undistributed Middle", to: "/FallacyLibraryPage#fallacy-undistributed-middle" },
    ],
    status: "logical",
    category: "logic",
  },
  {
    id: "valid",
    term: "Valid",
    definition: "The structural property of a deductive argument such that it is logically impossible for its premises to be true while its conclusion is false.",
    example: "'If it is a square, it is a rectangle. It is a square. Therefore, it is a rectangle.'",
    related: ["invalid", "sound", "deduction", "argument"],
    seeAlso: [
      { label: "Logic Laboratory", to: "/LogicLaboratoryPage" },
      { label: "Argument Tester", to: "/LogicLaboratoryPage#argument-tester" },
    ],
    status: "logical",
    category: "logic",
  },
  {
    id: "virtue-epistemology",
    term: "Virtue Epistemology",
    definition: "An approach to epistemology that evaluates the intellectual virtues, cognitive character traits, and conscientious motivations of the thinker rather than assessing beliefs in isolation.",
    example: "Cultivating open-mindedness, intellectual humility, and rigorous curiosity as personal guardrails against dogmatism.",
    related: ["epistemology", "reliabilism", "knowledge", "justification"],
    seeAlso: [
      { label: "Knowledge Laboratory", to: "/KnowledgeLaboratoryPage" },
      { label: "Critical Thinking Workshop", to: "/CriticalThinkingPage" },
    ],
    status: "philosophical",
    category: "epistemology",
  },
  {
    id: "weak-analogy",
    term: "Weak Analogy",
    definition: "An informal fallacy that assumes because two things are similar in one superficial respect, they must also be similar in another substantive, unrelated respect.",
    example: "'A watch has intricate gears and requires an intelligent watchmaker; the solar system has orbital bodies, so it must have gears.'",
    related: ["informal-fallacy", "fallacy", "induction"],
    seeAlso: [
      { label: "Fallacy: Weak Analogy", to: "/FallacyLibraryPage#fallacy-weak-analogy" },
      { label: "Critical Thinking Workshop", to: "/CriticalThinkingPage" },
    ],
    status: "philosophical",
    category: "reasoning",
  },
];
